import React, { useMemo, useState } from "react";
import { ExternalLink, Search } from "lucide-react";
import { Label, inputCls } from "@/components/argus/ui";

const q = encodeURIComponent;
export const DBS = [
  { group: "Companies", items: [
    ["OpenCorporates", n => `https://opencorporates.com/companies?q=${q(n)}`],
    ["UK Companies House", n => `https://find-and-update.company-information.service.gov.uk/search?q=${q(n)}`],
    ["SEC EDGAR filings", n => `https://www.sec.gov/edgar/search/#/q=${q(`"${n}"`)}`],
    ["ICIJ Offshore Leaks", n => `https://offshoreleaks.icij.org/search?q=${q(n)}`],
  ] },
  { group: "Courts & sanctions", items: [
    ["CourtListener (US courts)", n => `https://www.courtlistener.com/?q=${q(`"${n}"`)}`],
    ["OpenSanctions", n => `https://www.opensanctions.org/search/?q=${q(n)}`],
    ["OFAC sanctions list", () => "https://sanctionssearch.ofac.treas.gov/"],
  ] },
  { group: "Money & contracts", items: [
    ["USAspending (federal contracts)", n => `https://www.usaspending.gov/search/?hash=&keyword=${q(n)}`],
    ["OpenSecrets (political money)", n => `https://www.opensecrets.org/search?q=${q(n)}`],
    ["LittleSis (power networks)", n => `https://littlesis.org/search?q=${q(n)}`],
  ] },
  { group: "News & web", items: [
    ["Google News", n => `https://news.google.com/search?q=${q(`"${n}"`)}`],
    ["Bing News", n => `https://www.bing.com/news/search?q=${q(`"${n}"`)}`],
    ["Exact-phrase web search", n => `https://duckduckgo.com/?q=${q(`"${n}"`)}`],
  ] },
];

export default function ArgusLookups({ sources }) {
  const entities = useMemo(() => {
    const set = new Map();
    sources.forEach(s => {
      const x = s.extracted || {};
      (x.people || []).forEach(p => p?.name && set.set(p.name, "person"));
      (x.organizations || []).forEach(o => o && set.set(o, "organisation"));
    });
    return [...set.entries()].slice(0, 40);
  }, [sources]);
  const [name, setName] = useState(entities[0]?.[0] || "");
  return (
    <div className="space-y-4" data-testid="argus-lookups">
      <div className="p-4 rounded-lg argus-glass">
        <Label>Find public records about a person or company · free</Label>
        <div className="relative mt-2"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" /><input className={`${inputCls} !pl-9`} value={name} onChange={e => setName(e.target.value)} placeholder="Type a name, or pick one found in your sources" data-testid="argus-lookup-name" /></div>
        {entities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="argus-lookup-entities">
            {entities.map(([n, kind]) => <button key={n} type="button" onClick={() => setName(n)} className={`px-2 py-0.5 rounded-full border text-[11px] ${name === n ? "bg-[#10b981] text-black border-[#10b981]" : "border-white/15 text-white/70"}`} data-testid={`argus-lookup-entity-${n.replace(/\W+/g, "-").toLowerCase()}`}>{n} <span className="opacity-50">· {kind}</span></button>)}
          </div>
        )}
        {entities.length === 0 && <p className="text-[11px] text-white/40 mt-2">Tip: run "Extract facts" on a source and the people and companies it mentions appear here.</p>}
      </div>
      {name.trim().length > 1 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {DBS.map(g => (
            <div key={g.group} className="p-4 rounded-lg argus-glass">
              <Label>{g.group}</Label>
              <ul className="mt-2 space-y-1.5">
                {g.items.map(([label, fn]) => (
                  <li key={label}><a href={fn(name.trim())} target="_blank" rel="noopener noreferrer" className="text-sm text-white/85 hover:text-[#10b981] inline-flex items-center gap-1.5" data-testid={`argus-lookup-link-${label.replace(/\W+/g, "-").toLowerCase()}`}>{label} <ExternalLink className="w-3 h-3 opacity-50" /></a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-white/40">Links open each official database's own search in a new tab. Save useful pages back to the case as link sources.</p>
    </div>
  );
}
